import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import { Box, Card, CardContent, CardHeader, Stack, Typography } from "@mui/material";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import ExpandableBox from "@entrilia/shared/components/ExpandableBox";
import StatusChip from "@entrilia/shared/components/StatusChip";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import HtmlPreview from "@entrilia/shared/components/html/HtmlPreview";
import { tagColorToThemeColorMap } from "@entrilia/shared/utilities/colorHelper";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { Note } from "../../api/types/notesTypes";
import useUserContext from "../../context/useUserContext";
import { useNotesContext } from "./NotesContext";

interface Props {
  note: Note;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const NoteCard = ({ note, onEdit, onDelete }: Props) => {
  const { hasPermissionsToManageNotes } = useNotesContext();
  const { id: userId } = useUserContext();

  const handleEditClick = () => {
    onEdit(note.id);
  };

  const handleDeleteClick = () => {
    onDelete(note.id);
  };

  const isCreatedByCurrentUser = note.createdBy === userId;

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          sx={{ pb: 1 }}
          avatar={note.createdByUsername && <UserAvatar size="small" userName={note.createdByUsername} />}
          title={
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="subtitle2">{isCreatedByCurrentUser ? "You" : note.createdByUsername}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {convertISODateTime(note.updatedAt)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                {note.systemTag && (
                  <StatusChip label={note.systemTag} color={tagColorToThemeColorMap[note.systemTagColor ?? "None"]} />
                )}
                {hasPermissionsToManageNotes && (
                  <ActionsMenuButton
                    items={[
                      {
                        disabled: !isCreatedByCurrentUser,
                        icon: <EditIcon color="secondary" />,
                        label: "Edit",
                        onClick: handleEditClick,
                      },
                      {
                        icon: <DeleteIcon color="error" />,
                        label: "Delete",
                        onClick: handleDeleteClick,
                      },
                    ]}
                  />
                )}
              </Stack>
            </Box>
          }
        />
        <CardContent sx={{ pt: 0, pb: 1.5, pl: 7 }}>
          <ExpandableBox maxHeight={100}>
            <HtmlPreview htmlContent={note.htmlContent} />
          </ExpandableBox>
        </CardContent>
      </Card>
    </>
  );
};

export default NoteCard;
